import LoadingButton from "@mui/lab/LoadingButton"
import Button from "@mui/material/Button"
import Checkbox from "@mui/material/Checkbox"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import FormControlLabel from "@mui/material/FormControlLabel"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import { useEffect, useMemo, useState } from "react"

import Column from "../../../common/components/Column"
import Row from "../../../common/components/Row"
import DialogHeader from "../components/DialogHeader"
import {
  retryableDialerLeads,
  sessionOutcomeRows,
  sessionSummaryCounts,
} from "./powerDialer"

export default function PowerDialerSummaryDialog({
  open,
  session,
  isRetrying = false,
  showDailyReport = false,
  isOpeningDailyReport = false,
  onClose,
  onRetry,
  onOpenDailyReport,
}) {
  const [includeBusy, setIncludeBusy] = useState(false)
  const [retryName, setRetryName] = useState("")
  const { logged, skipped, booked, attempted } = sessionSummaryCounts(session)
  const remaining = Number(session?.remaining_count) || 0
  const outcomes = sessionOutcomeRows(session)
  const retryLeads = useMemo(
    () => retryableDialerLeads(session, { includeBusy }),
    [includeBusy, session]
  )
  const noAnswerCount = Number(session?.outcome_counts?.no_answer) || 0
  const busyCount = Number(session?.outcome_counts?.busy) || 0
  const canRetry = retryLeads.length > 0 && typeof onRetry === "function"

  useEffect(() => {
    if (!open) return
    setIncludeBusy(false)
    const existing = String(session?.name || "").trim()
    setRetryName(existing ? `${existing} retry` : "")
  }, [open, session?.id, session?.name])

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      sx={{ zIndex: 1700 }}
    >
      <DialogHeader
        title={session?.name ? `${session.name} complete` : "Session complete"}
        onClose={onClose}
      />
      <DialogContent>
        <Column gap={2} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Here is how this Power Dialer session finished.
          </Typography>
          <Row sx={{ gap: 1, flexWrap: "wrap" }}>
            <SummaryStat label="Logged" value={logged} />
            <SummaryStat label="Skipped" value={skipped} />
            <SummaryStat label="Booked" value={booked} />
            <SummaryStat label="Attempted" value={attempted} emphasis />
          </Row>
          {remaining > 0 ? (
            <Typography variant="body2" color="text.secondary">
              {remaining} lead{remaining === 1 ? " was" : "s were"} not called.
            </Typography>
          ) : null}
          <Column gap={1}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 700, letterSpacing: "0.06em" }}
            >
              DISPOSITIONS
            </Typography>
            {outcomes.length ? (
              outcomes.map((row) => (
                <Row
                  key={row.value}
                  sx={{
                    justifyContent: "space-between",
                    alignItems: "center",
                    px: 1.25,
                    py: 0.85,
                    borderRadius: 1.5,
                    bgcolor: "colors.gray50",
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {row.label}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 800 }}>
                    {row.count}
                  </Typography>
                </Row>
              ))
            ) : (
              <Typography variant="body2" color="text.secondary">
                No call outcomes were logged.
              </Typography>
            )}
          </Column>
          {noAnswerCount > 0 || busyCount > 0 ? (
            <Column gap={0.5}>
              {busyCount > 0 ? (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={includeBusy}
                      onChange={(e) => setIncludeBusy(e.target.checked)}
                    />
                  }
                  label={`Also retry busy (${busyCount})`}
                />
              ) : null}
              <Typography variant="body2" color="text.secondary">
                Call no-answers again without re-checking the list.
              </Typography>
              <TextField
                required
                fullWidth
                size="small"
                label="Retry session name"
                value={retryName}
                onChange={(e) => setRetryName(e.target.value)}
                inputProps={{ maxLength: 128 }}
              />
            </Column>
          ) : null}
        </Column>
      </DialogContent>
      <DialogActions sx={{ flexWrap: "wrap", gap: 1 }}>
        <Button variant="outlined" color="primary" onClick={onClose}>
          Done
        </Button>
        {showDailyReport ? (
          <LoadingButton
            variant="contained"
            color="primary"
            loading={isOpeningDailyReport}
            onClick={onOpenDailyReport}
          >
            Submit Daily Report
          </LoadingButton>
        ) : null}
        {canRetry ? (
          <LoadingButton
            variant="contained"
            color="primary"
            loading={isRetrying}
            disabled={!retryName.trim()}
            onClick={() =>
              onRetry(
                retryLeads.map((lead) => String(lead.lead_ref_id)),
                retryName.trim()
              )
            }
          >
            Call no-answers again ({retryLeads.length})
            {includeBusy ? " + busy" : ""}
          </LoadingButton>
        ) : null}
      </DialogActions>
    </Dialog>
  )
}

function SummaryStat({ label, value, emphasis = false }) {
  return (
    <Column
      sx={{
        flex: "1 1 72px",
        minWidth: 72,
        p: 1.25,
        borderRadius: 1.5,
        alignItems: "center",
        bgcolor: emphasis ? "colors.brand50" : "colors.gray50",
        border: "1px solid",
        borderColor: emphasis ? "colors.brand200" : "colors.gray200",
      }}
    >
      <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
        {value}
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontWeight: 700 }}
      >
        {label}
      </Typography>
    </Column>
  )
}
